import { useMemo } from 'react';
import { StoreProductDetail } from '../models';
import { useBabySizeMatches } from './useBabySizeMatches';
import { productFitsChild, sizeBadgeLabel } from '../utils/babyAge';
import { AGE_VARIATION_TYPE } from '../utils/variantResolution';

interface ProductBabySize {
  badgeByValue: Map<string, string>;
  recommendedValues: string[];
}

const EMPTY_RESULT: ProductBabySize = { badgeByValue: new Map(), recommendedValues: [] };

export function useProductBabySize(product: StoreProductDetail | null): ProductBabySize {
  const matches = useBabySizeMatches();

  return useMemo(() => {
    if (!product || matches.length === 0) return EMPTY_RESULT;

    const productAgeValues = new Set(
      product.variants.filter((v) => v.variationTypeId === AGE_VARIATION_TYPE).map((v) => v.variationValue)
    );
    if (productAgeValues.size === 0) return EMPTY_RESULT;

    const babiesByValue = new Map<string, typeof matches[number]['baby'][]>();
    for (const match of matches) {
      if (!productFitsChild(product.gender, match.baby)) continue;
      for (const option of match.options) {
        if (!productAgeValues.has(option.code)) continue;
        babiesByValue.set(option.code, [...(babiesByValue.get(option.code) ?? []), match.baby]);
      }
    }

    const badgeByValue = new Map<string, string>();
    babiesByValue.forEach((babies, value) => badgeByValue.set(value, sizeBadgeLabel(babies)));

    return { badgeByValue, recommendedValues: Array.from(babiesByValue.keys()) };
  }, [product, matches]);
}
